import React, { useState, useEffect } from 'react';
import { TrendingUp, Briefcase, Calendar, Camera, PoundSterling, Award } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { money } from '../../lib/format';

const StaffQuickStats = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="quick-stats-loading">
        {[1, 2, 3, 4].map(i => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="bd-skel h-3 w-20 mb-3"></div>
              <div className="bd-skel h-6 w-16"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      label: `${stats.month_name} earnings`,
      value: money(stats.monthly_stats.total_earnings),
      subtext: `${stats.monthly_stats.completed_jobs} job${stats.monthly_stats.completed_jobs === 1 ? '' : 's'}`,
      icon: PoundSterling,
    },
    {
      label: 'Upcoming jobs',
      value: stats.upcoming_jobs,
      subtext: 'scheduled',
      icon: Calendar,
    },
    {
      label: 'All-time jobs',
      value: stats.all_time_stats.completed_jobs,
      subtext: `${money(stats.all_time_stats.total_earnings)} earned`,
      icon: Briefcase,
    },
    {
      label: 'Photo bonuses',
      value: stats.bonus_eligible_photos,
      subtext: `${stats.photos_uploaded} total photos`,
      icon: Camera,
    }
  ];

  return (
    <div className="mb-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className="bd-card-accent bd-fade-up bg-white transition-shadow hover:shadow-gold"
            style={{ animationDelay: `${index * 70}ms` }}
            data-testid={`quick-stat-${index}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{stat.label}</p>
                  <p className="mt-1 text-xl font-black tabular-nums text-ink md:text-2xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-slate-500">{stat.subtext}</p>
                </div>
                <div className="rounded-full bg-cream p-2">
                  <stat.icon className="h-4 w-4 text-gold-deep" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Breakdown */}
      {stats.monthly_stats.total_earnings > 0 && (
        <Card className="border-gold/25 bg-ink text-cream">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gold" />
                <h3 className="text-sm">{stats.month_name} breakdown</h3>
              </div>
              <span className="text-2xl font-black tabular-nums text-gold">
                {money(stats.monthly_stats.total_earnings)}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="rounded-[10px] bg-white/10 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-cream/60">Wages</p>
                <p className="text-lg font-bold tabular-nums">{money(stats.monthly_stats.wage)}</p>
              </div>
              <div className="rounded-[10px] bg-white/10 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-cream/60">Travel</p>
                <p className="text-lg font-bold tabular-nums">{money(stats.monthly_stats.travel)}</p>
              </div>
              <div className="rounded-[10px] bg-white/10 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-cream/60">Bonuses</p>
                <p className="text-lg font-bold tabular-nums text-gold">{money(stats.monthly_stats.bonus)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Achievement Badge */}
      {stats.all_time_stats.completed_jobs >= 10 && (
        <div className="mt-3 flex items-center gap-2 rounded-[10px] border border-gold bg-cream p-3">
          <Award className="h-5 w-5 shrink-0 text-gold-deep" />
          <span className="text-sm text-ink">
            <strong>Seasoned pro.</strong> You've completed {stats.all_time_stats.completed_jobs} jobs — thank you.
          </span>
        </div>
      )}
    </div>
  );
};

export default StaffQuickStats;
